import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import MonacoEditor from '@monaco-editor/react';
import { schemaService } from '../../services/schemaService';
import { categoryService } from '../../services/categoryService';
import CategoryTreeSelector from '../../components/CategoryTreeSelector';
import DynamicFormBuilder from '../../components/DynamicFormBuilder';

// Suppress ResizeObserver errors (common with Monaco Editor)
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
    return;
  }
  originalError.apply(console, args);
};

const SchemaManager = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Schemas
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  
  // Editor
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Inheritance fields
  const [isBaseSchema, setIsBaseSchema] = useState(false);
  const [parentSchemaId, setParentSchemaId] = useState(null);
  const [availableParentSchemas, setAvailableParentSchemas] = useState([]);
  
  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState(null);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [categorySelectorOpen, setCategorySelectorOpen] = useState(false);
  
  // Form preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // View mode (from URL parameter)
  const viewMode = searchParams.get('mode') === 'view';

  // Load categories (top-level only for better performance)
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await categoryService.getCategoryTreeLazy();
      
      if (response.success) {
        // Handle the double-nested response structure
        const categoriesData = response.data?.data || response.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } else {
        setError('Failed to load categories');
        setCategories([]);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Error loading categories: ' + err.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load schemas for selected category
  const loadSchemas = useCallback(async (categoryId) => {
    if (!categoryId) return;
    
    try {
      setLoading(true);
      const response = await schemaService.getSchemasByCategory(categoryId);
      if (response.success) {
        // Ensure data is an array
        const schemasData = Array.isArray(response.data) ? response.data : [];
        setSchemas(schemasData);
      } else {
        setError('Failed to load schemas');
        setSchemas([]); // Set empty array on error
      }
    } catch (err) {
      setError('Error loading schemas: ' + err.message);
      setSchemas([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Load available parent schemas
  const loadAvailableParentSchemas = useCallback(async () => {
    try {
      const response = await schemaService.getSchemas();
      if (response.success) {
        const allSchemas = response.data.results || response.data || [];
        // Filter out only the current schema (all other schemas can be parents)
        const availableSchemas = allSchemas.filter(schema => 
          schema.sid !== selectedSchema?.sid
        );
        setAvailableParentSchemas(availableSchemas);
      } else {
        setAvailableParentSchemas([]);
      }
    } catch (err) {
      console.error('Error loading parent schemas:', err);
      setAvailableParentSchemas([]);
    }
  }, [selectedSchema]);

  // Load schema details
  const loadSchemaDetails = useCallback(async (schemaId) => {
    try {
      setLoading(true);
      const response = await schemaService.getSchema(schemaId);
      if (response.success) {
        setSelectedSchema(response.data);
        setJsonValue(JSON.stringify(response.data.config_json, null, 2));
        setJsonError(null);
        // Set inheritance fields
        setIsBaseSchema(response.data.is_base_schema || false);
        setParentSchemaId(response.data.parent_config || null);
        
        // Set the selected category from the schema data
        // This is needed for the Save/Update button to be enabled
        if (response.data.category) {
          // The category field contains the SID, we need to create a category object
          setSelectedCategory({
            sid: response.data.category,
            name: response.data.category_name || response.data.category_name_display
          });
        }
      } else {
        setError('Failed to load schema details');
      }
    } catch (err) {
      setError('Error loading schema details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate JSON
  const validateJson = useCallback(async (jsonString) => {
    try {
      const response = await schemaService.validateJson({ config_json: JSON.parse(jsonString) });
      if (response.success) {
        setJsonError(null);
        return true;
      } else {
        setJsonError(response.error || 'Invalid JSON schema');
        return false;
      }
    } catch (err) {
      setJsonError('Invalid JSON format');
      return false;
    }
  }, []);

  // Save schema
  const saveSchema = useCallback(async () => {
    if (!selectedCategory) {
      setError('Please select a category first');
      return;
    }

    try {
      setLoading(true);
      const isValid = await validateJson(jsonValue);
      if (!isValid) {
        setError('Please fix JSON errors before saving');
        return;
      }

      const configData = {
        category: selectedCategory.sid,
        category_name: selectedCategory.name,
        config_json: JSON.parse(jsonValue),
        is_active: true,
        is_base_schema: isBaseSchema,
        parent_config: parentSchemaId || null
      };

      let response;
      if (selectedSchema) {
        // Update existing schema
        response = await schemaService.updateSchema(selectedSchema.sid, configData);
      } else {
        // Create new schema
        response = await schemaService.createSchema(configData);
      }

      if (response.success) {
        setSuccess('Schema saved successfully!');
        setSelectedSchema(response.data);
        loadSchemas(selectedCategory.sid);
        setIsEditing(false);
      } else {
        setError(response.error || 'Failed to save schema');
      }
    } catch (err) {
      setError('Error saving schema: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, jsonValue, selectedSchema, isBaseSchema, parentSchemaId, validateJson, loadSchemas]);

  // Delete schema
  const deleteSchema = useCallback(async (schemaId) => {
    try {
      setLoading(true);
      const response = await schemaService.deleteSchema(schemaId);
      if (response.success) {
        setSuccess('Schema deleted successfully!');
        loadSchemas(selectedCategory.sid);
        if (selectedSchema && selectedSchema.sid === schemaId) {
          setSelectedSchema(null);
          setJsonValue('');
        }
      } else {
        setError(response.error || 'Failed to delete schema');
      }
    } catch (err) {
      setError('Error deleting schema: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedSchema, loadSchemas]);

  // Initialize
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Load schemas when category changes
  useEffect(() => {
    if (selectedCategory) {
      loadSchemas(selectedCategory.sid);
    }
  }, [selectedCategory, loadSchemas]);

  // Load parent schemas when page loads or when category changes
  useEffect(() => {
    loadAvailableParentSchemas();
  }, [loadAvailableParentSchemas]);

  // Load schema from URL parameter (for view/edit from schema list)
  useEffect(() => {
    const schemaSid = searchParams.get('schema');
    if (schemaSid) {
      // Load the schema by SID
      loadSchemaDetails(schemaSid);
      // If in view mode, disable editing
      if (viewMode) {
        setIsEditing(false);
      }
    }
  }, [searchParams, viewMode, loadSchemaDetails]);

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedSchema(null);
    setJsonValue('');
    setJsonError(null);
    setIsEditing(false);
  };

  // Handle schema selection
  const handleSchemaSelect = (schema) => {
    setSelectedSchema(schema);
    loadSchemaDetails(schema.sid);
    setIsEditing(false);
  };

  // Handle JSON change
  const handleJsonChange = (value) => {
    setJsonValue(value);
    // Auto-validate JSON
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError('Invalid JSON syntax');
    }
  };

  // Handle preview
  const handlePreview = () => {
    try {
      const config = JSON.parse(jsonValue);
      setPreviewData(config);
      setShowPreview(true);
    } catch (err) {
      setError('Cannot preview: Invalid JSON');
    }
  };

  // Handle create new schema
  const handleCreateNew = () => {
    if (!selectedCategory) {
      setError('Please select a category first');
      return;
    }
    
    setSelectedSchema(null);
    setIsBaseSchema(false);
    setParentSchemaId(null);
    // Start with an empty schema structure
    setJsonValue(JSON.stringify({
      "category": selectedCategory.name,
      "description": "Enter schema description here",
      "sections": []
    }, null, 2));
    setJsonError(null);
    setIsEditing(true);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.palette.background.default,
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
          Schema Manager
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage category-specific product form schemas
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Panel - Category & Schema List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardHeader 
              title="Categories & Schemas"
              action={
                <Tooltip title="Refresh">
                  <IconButton onClick={() => loadCategories()}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              {/* Category Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Selected Category
                </Typography>
                {selectedCategory ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 2,
                    border: 1,
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }}>
                    <FolderIcon />
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedCategory.name}
                    </Typography>
                    <Chip 
                      label="Selected" 
                      size="small" 
                      color="success" 
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'grey.50'
                  }}>
                    <FolderIcon color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      No category selected
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setCategorySelectorOpen(true)}
                  sx={{ mt: 1 }}
                >
                  {selectedCategory ? 'Change Category' : 'Select Category'}
                </Button>
              </Box>

              {/* Create New Button */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                disabled={!selectedCategory}
                fullWidth
                sx={{ mb: 3 }}
              >
                Create New Schema
              </Button>

              {/* Schema List */}
              {selectedCategory && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Existing Schemas
                  </Typography>
                  {!Array.isArray(schemas) || schemas.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No schemas found for this category
                    </Typography>
                  ) : (
                    <List>
                      {schemas.map((schema) => (
                        <ListItem
                          key={schema.sid}
                          button
                          selected={selectedSchema?.sid === schema.sid}
                          onClick={() => handleSchemaSelect(schema)}
                          sx={{ mb: 1, borderRadius: 1 }}
                        >
                          <ListItemText
                            primary={`Version ${schema.version}`}
                            secondary={
                              <Box>
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                                  {schema.is_active ? (
                                    <Chip label="Active" size="small" color="success" />
                                  ) : (
                                    <Chip label="Inactive" size="small" color="default" />
                                  )}
                                  {schema.is_base_schema && (
                                    <Chip label="Base Schema" size="small" color="primary" />
                                  )}
                                  {schema.parent_config && (
                                    <Chip label="Inherits" size="small" color="info" />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(schema.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSchemaToDelete(schema);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel - JSON Editor & Preview */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardHeader 
              title="Schema Editor"
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                    disabled={!jsonValue || !!jsonError}
                    size="small"
                  >
                    Preview
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveSchema}
                    disabled={viewMode || !selectedCategory || !jsonValue || !!jsonError || loading}
                    size="small"
                  >
                    {selectedSchema ? 'Update' : 'Save'}
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {/* Inheritance Settings */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Schema Inheritance Settings
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isBaseSchema}
                          onChange={(e) => setIsBaseSchema(e.target.checked)}
                          disabled={selectedSchema?.is_base_schema}
                        />
                      }
                      label="Mark as Base Schema"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Base schemas serve as fallback templates for categories without specific schemas
                    </Typography>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Parent Schema (Optional)</InputLabel>
                      <Select
                        value={parentSchemaId || ''}
                        onChange={(e) => setParentSchemaId(e.target.value || null)}
                        label="Parent Schema (Optional)"
                        disabled={isBaseSchema}
                      >
                        <MenuItem value="">
                          <em>No parent schema</em>
                        </MenuItem>
                        {availableParentSchemas.map((schema) => (
                          <MenuItem key={schema.sid} value={schema.sid}>
                            {schema.category_name} (v{schema.version})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Inherit fields from another schema. Base schemas cannot have parents.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* JSON Editor */}
              <Box sx={{ height: 500, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <MonacoEditor
                  height="100%"
                  language="json"
                  value={jsonValue}
                  onChange={handleJsonChange}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    insertSpaces: true,
                    wordWrap: 'on',
                    lineNumbers: 'on',
                    folding: true,
                    bracketMatching: 'always',
                    readOnly: viewMode, // Make read-only in view mode
                    // Prevent ResizeObserver issues
                    resizeObserver: false,
                    // Additional stability options
                    renderLineHighlight: 'line',
                    cursorBlinking: 'blink',
                    cursorSmoothCaretAnimation: false,
                  }}
                  theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                  onMount={(editor, monaco) => {
                    // Additional configuration on mount
                    editor.layout();
                  }}
                />
              </Box>

              {/* JSON Error Display */}
              {jsonError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {jsonError}
                </Alert>
              )}

              {/* Editor Actions */}
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedSchema ? `Editing: ${selectedSchema.category_name} v${selectedSchema.version}` : 'Creating new schema'}
                </Typography>
                {selectedSchema && (
                  <Button
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => setVersionDialogOpen(true)}
                    size="small"
                  >
                    Versions
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Form Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Form Preview</DialogTitle>
        <DialogContent>
          <DynamicFormBuilder
            schema={previewData}
            mode="preview"
            readOnly={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Schema</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this schema? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              deleteSchema(schemaToDelete.sid);
              setDeleteDialogOpen(false);
            }}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Tree Selector Dialog */}
      <CategoryTreeSelector
        open={categorySelectorOpen}
        onClose={() => setCategorySelectorOpen(false)}
        onSelect={handleCategorySelect}
        selectedCategory={selectedCategory}
        title="Select Category for Schema"
      />

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default SchemaManager;
